import { useMemo, useState, useCallback } from 'react';
import {
  useDailySpending,
  type DailySpending,
} from '../../hooks/useDailySpending';
import { formatMXN } from '../../utils/format';

interface CalendarHeatmapProps {
  year?: number;
  onDayClick?: (date: string) => void;
}

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  date: string;
  amount: number;
}

const CELL_SIZE = 12;
const CELL_GAP = 2;
const CELL_TOTAL = CELL_SIZE + CELL_GAP;
const MONTH_LABEL_HEIGHT = 20;
const DAY_LABEL_WIDTH = 24;
const WEEKS_IN_YEAR = 53;
const DAYS_IN_WEEK = 7;

const DAY_LABELS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const MONTH_LABELS = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

const COLOR_SCALE = [
  'rgb(235, 237, 240)',
  'rgb(198, 228, 139)',
  'rgb(123, 201, 111)',
  'rgb(35, 154, 59)',
  'rgb(25, 97, 39)',
];

function getColorForAmount(amount: number): string {
  if (amount === 0) return COLOR_SCALE[0];
  if (amount <= 500) return COLOR_SCALE[1];
  if (amount <= 1000) return COLOR_SCALE[2];
  if (amount <= 3000) return COLOR_SCALE[3];
  return COLOR_SCALE[4];
}

function getWeekNumber(date: Date, yearStart: Date): number {
  const diff = date.getTime() - yearStart.getTime();
  const oneDay = 24 * 60 * 60 * 1000;
  const dayOfYear = Math.floor(diff / oneDay);
  return Math.floor((dayOfYear + yearStart.getDay()) / 7);
}

function formatTooltipDate(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  const day = date.getDate();
  const month = MONTH_LABELS[date.getMonth()];
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
}

function generateYearDays(
  year: number
): { date: string; dayOfWeek: number; week: number }[] {
  const days: { date: string; dayOfWeek: number; week: number }[] = [];
  const yearStart = new Date(year, 0, 1);

  for (
    let d = new Date(year, 0, 1);
    d.getFullYear() === year;
    d.setDate(d.getDate() + 1)
  ) {
    const dateStr = d.toISOString().slice(0, 10);
    days.push({
      date: dateStr,
      dayOfWeek: d.getDay(),
      week: getWeekNumber(d, yearStart),
    });
  }

  return days;
}

function getMonthPositions(year: number): { month: number; week: number }[] {
  const positions: { month: number; week: number }[] = [];
  const yearStart = new Date(year, 0, 1);

  for (let month = 0; month < 12; month++) {
    const firstOfMonth = new Date(year, month, 1);
    const week = getWeekNumber(firstOfMonth, yearStart);
    positions.push({ month, week });
  }

  return positions;
}

export function CalendarHeatmap({ year, onDayClick }: CalendarHeatmapProps) {
  const currentYear = year ?? new Date().getFullYear();
  const startDate = `${currentYear}-01-01`;
  const endDate = `${currentYear}-12-31`;

  const dailyData = useDailySpending({ startDate, endDate });

  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    date: '',
    amount: 0,
  });

  const { dataMap, yearDays, monthPositions } = useMemo(() => {
    const map = new Map<string, DailySpending>();

    dailyData.forEach((d) => {
      map.set(d.date, d);
    });

    return {
      dataMap: map,
      yearDays: generateYearDays(currentYear),
      monthPositions: getMonthPositions(currentYear),
    };
  }, [dailyData, currentYear]);

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<SVGRectElement>, date: string, amount: number) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltip({
        visible: true,
        x: rect.left + rect.width / 2,
        y: rect.top - 8,
        date,
        amount,
      });
    },
    []
  );

  const handleMouseLeave = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  const handleClick = useCallback(
    (date: string) => {
      onDayClick?.(date);
    },
    [onDayClick]
  );

  const svgWidth = DAY_LABEL_WIDTH + WEEKS_IN_YEAR * CELL_TOTAL;
  const svgHeight = MONTH_LABEL_HEIGHT + DAYS_IN_WEEK * CELL_TOTAL;

  return (
    <div className="relative" data-testid="calendar-heatmap">
      <div className="overflow-x-auto pb-2">
        <svg
          width={svgWidth}
          height={svgHeight}
          className="block"
          role="img"
          aria-label="Mapa de actividad diaria de gastos"
        >
          {monthPositions.map(({ month, week }) => (
            <text
              key={month}
              x={DAY_LABEL_WIDTH + week * CELL_TOTAL}
              y={MONTH_LABEL_HEIGHT - 6}
              className="fill-gray-500 text-[10px]"
            >
              {MONTH_LABELS[month]}
            </text>
          ))}

          {[0, 2, 4, 6].map((dayIndex) => (
            <text
              key={dayIndex}
              x={DAY_LABEL_WIDTH - 8}
              y={MONTH_LABEL_HEIGHT + dayIndex * CELL_TOTAL + CELL_SIZE}
              className="fill-gray-500 text-[10px]"
              textAnchor="end"
            >
              {DAY_LABELS[dayIndex]}
            </text>
          ))}

          {yearDays.map(({ date, dayOfWeek, week }) => {
            const spending = dataMap.get(date);
            const amount = spending?.amount ?? 0;
            const color = getColorForAmount(amount);

            return (
              <rect
                key={date}
                data-testid="heatmap-cell"
                data-date={date}
                x={DAY_LABEL_WIDTH + week * CELL_TOTAL}
                y={MONTH_LABEL_HEIGHT + dayOfWeek * CELL_TOTAL}
                width={CELL_SIZE}
                height={CELL_SIZE}
                rx={2}
                fill={color}
                className="cursor-pointer transition-opacity hover:opacity-80"
                onMouseEnter={(e) => handleMouseEnter(e, date, amount)}
                onMouseLeave={handleMouseLeave}
                onClick={() => handleClick(date)}
              />
            );
          })}
        </svg>
      </div>

      {tooltip.visible && (
        <div
          role="tooltip"
          className="fixed z-50 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded shadow-lg pointer-events-none transform -translate-x-1/2 -translate-y-full"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <div>{formatTooltipDate(tooltip.date)}</div>
          <div className="text-center">{formatMXN(tooltip.amount)}</div>
        </div>
      )}

      <div className="flex items-center justify-end gap-1 mt-2 text-xs text-gray-500">
        <span>Menos</span>
        {COLOR_SCALE.map((color, i) => (
          <div
            key={i}
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: color }}
          />
        ))}
        <span>Más</span>
      </div>
    </div>
  );
}
